import {
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { api } from '../lib/api';
import { eventRangeQueryOptions } from '../lib/eventQueries';
import { useAllCalendars } from './AllCalendarsContext';
import {
  useSubscriptions,
  useCreateSubscription,
} from '../hooks/useSubscriptions';
import {
  expandEvents,
  resolveEventForEdit,
  type ExpandedEvent,
} from '../lib/rrule';
import {
  startOfMonth,
  daysInMonth,
  startOfDay,
} from '../lib/dateUtils';
import { useHolidaysByDay } from '../hooks/useHolidays';

export type MultiDaySegment = {
  event: ExpandedEvent;
  color: string;
  rowIndex: number;
  colStart: number;
  colSpan: number;
  isStart: boolean;
  isEnd: boolean;
  stackIndex: number;
};

interface CalendarPageContextValue {
  calendarId: string;

  currentMonth: Date;
  prevMonth: () => void;
  nextMonth: () => void;
  goToToday: () => void;

  // Join token access state
  accessVia: 'membership' | 'join_token' | null;
  isMember: boolean;
  isTemporaryVisitor: boolean;
  joinToken: string | null;

  createForDate: Date | null;
  setCreateForDate: (d: Date | null) => void;
  editEventId: string | null;
  setEditEventId: (id: string | null) => void;
  editOccurrenceDate: string | null;
  setEditOccurrenceDate: (d: string | null) => void;

  expandedDay: Date | null;
  setExpandedDay: (d: Date | null) => void;

  panelOpen: boolean;
  closePanel: () => void;
  closeDayModal: () => void;
  handleDayClick: (day: number) => void;
  selectEventForEdit: (ev: ExpandedEvent) => void;
  handleModalEventClick: (ev: ExpandedEvent) => void;
  handleModalCreateEvent: () => void;

  expandedEvents: ExpandedEvent[];
  eventsOnDay: (day: number) => ExpandedEvent[];
  multiDaySegments: MultiDaySegment[];
  multiDayOnDay: (day: number) => ExpandedEvent[];
  eventsOnOverflowDate: (date: Date) => ExpandedEvent[];
  navigateToOverflowDate: (date: Date) => void;

  leadingBlanks: number;
  totalDays: number;
  from: Date;
  to: Date;

  calendarColorMap: Record<string, string>;
  calColor: string;
  currentCalendar: ReturnType<
    ReturnType<typeof useAllCalendars>['getCalendar']
  >;

  showTrialBanner: boolean;
  trialBannerDismissed: boolean;
  setTrialBannerDismissed: (v: boolean) => void;
  billingData:
    | {
        plan: {
          status: string;
          trial_ends_at?: string | null;
        };
      }
    | undefined;

  holidayRegion: string | null | undefined;
  holidaysByDay: Map<number, string[]>;

  showSubscribeButton: boolean | string | null | undefined;
  isSubscribed: boolean;
  existingSubscription:
    | import('commoncal-shared').CalendarSubscription
    | undefined;
  createSubscription: ReturnType<
    typeof useCreateSubscription
  >;

  navigate: ReturnType<typeof useNavigate>;

  // Events loading state
  eventsIsLoading: boolean;
  eventsIsFetching: boolean;
  eventsIsError: boolean;
  eventsError: Error | null;
  refetchEvents: () => void;
}

// ── Pure date-matching helpers (module scope so they can be reused for
// both the current month and adjacent-month overflow days) ──
function eventsMatchingDate(
  list: ExpandedEvent[],
  date: Date
): ExpandedEvent[] {
  const y = date.getFullYear();
  const m = date.getMonth();
  const day = date.getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  const targetDateStr = `${y}-${pad(m + 1)}-${pad(day)}`;

  return list.filter(e => {
    // ── All-day events: timezone-free, compare YYYY-MM-DD strings ──
    if (e.all_day && e.start_date) {
      const sDate = e.start_date.slice(0, 10);
      const eDate = e.end_date?.slice(0, 10);
      if (eDate && sDate !== eDate) {
        return (
          targetDateStr >= sDate && targetDateStr <= eDate
        );
      }
      return sDate === targetDateStr;
    }
    // ── Timed events: stored in UTC, compare local date components ──
    const start = new Date(e.start_at);
    return (
      start.getFullYear() === y &&
      start.getMonth() === m &&
      start.getDate() === day
    );
  });
}

function multiDayEventsMatchingDate(
  list: ExpandedEvent[],
  date: Date
): ExpandedEvent[] {
  const MS_PER_DAY = 86400000;
  const target = startOfDay(date);
  return list.filter(e => {
    let eStart: Date;
    let eEnd: Date;
    if (e.all_day && e.start_date && e.end_date) {
      if (e.start_date === e.end_date) return false;
      eStart = startOfDay(
        new Date(e.start_date + 'T12:00')
      );
      eEnd = startOfDay(new Date(e.end_date + 'T12:00'));
    } else {
      const dur =
        new Date(e.end_at).getTime() -
        new Date(e.start_at).getTime();
      if (dur <= MS_PER_DAY) return false;
      eStart = startOfDay(new Date(e.start_at));
      eEnd = startOfDay(new Date(e.end_at));
    }
    return eStart <= target && eEnd >= target;
  });
}

const CalendarPageContext =
  createContext<CalendarPageContextValue | null>(null);

export function useCalendarPage(): CalendarPageContextValue {
  const ctx = useContext(CalendarPageContext);
  if (!ctx) {
    throw new Error(
      'useCalendarPage must be used within CalendarPageProvider'
    );
  }
  return ctx;
}

interface CalendarPageProviderProps {
  calendarId: string;
  joinToken?: string | null;
  isMember?: boolean;
  accessVia?: 'membership' | 'join_token' | null;
  initialMonth?: Date;
  children: ReactNode;
}

export function CalendarPageProvider({
  calendarId,
  joinToken,
  isMember: isMemberProp,
  accessVia: accessViaProp,
  initialMonth,
  children,
}: CalendarPageProviderProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    getCalendar,
    getCalendarColor,
    visibleCalendarIds,
  } = useAllCalendars();

  // Parse month from URL or use initialMonth prop or default to today
  const getInitialMonth = (): Date => {
    const monthParam = searchParams.get('month');
    if (monthParam) {
      // Parse YYYY-MM format
      const [year, month] = monthParam
        .split('-')
        .map(Number);
      if (year && month && month >= 1 && month <= 12) {
        return startOfMonth(new Date(year, month - 1, 1));
      }
    }
    return initialMonth ?? startOfMonth(new Date());
  };

  const [currentMonth, setCurrentMonth] =
    useState(getInitialMonth);
  const [createForDate, setCreateForDate] =
    useState<Date | null>(null);
  const [editEventId, setEditEventId] = useState<
    string | null
  >(null);
  const [editOccurrenceDate, setEditOccurrenceDate] =
    useState<string | null>(null);
  const [expandedDay, setExpandedDay] =
    useState<Date | null>(null);
  const [trialBannerDismissed, setTrialBannerDismissed] =
    useState(false);

  const monthStart = startOfMonth(currentMonth);
  const leadingBlanks = monthStart.getDay();
  const totalDays = daysInMonth(currentMonth);

  const from = useMemo(
    () =>
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        1
      ),
    [currentMonth]
  );
  const to = useMemo(
    () =>
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        0,
        23,
        59,
        59
      ),
    [currentMonth]
  );

  // ── Adjacent months, used to fill leading/trailing overflow cells in
  // the month grid. These reuse the same query key shape as the main
  // month query, so they hit the prefetch cache in the common case.
  const prevMonthDate = useMemo(
    () =>
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() - 1,
        1
      ),
    [currentMonth]
  );
  const nextMonthDate = useMemo(
    () =>
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + 1,
        1
      ),
    [currentMonth]
  );
  const prevFrom = useMemo(
    () =>
      new Date(
        prevMonthDate.getFullYear(),
        prevMonthDate.getMonth(),
        1
      ),
    [prevMonthDate]
  );
  const prevTo = useMemo(
    () =>
      new Date(
        prevMonthDate.getFullYear(),
        prevMonthDate.getMonth() + 1,
        0,
        23,
        59,
        59
      ),
    [prevMonthDate]
  );
  const nextFrom = useMemo(
    () =>
      new Date(
        nextMonthDate.getFullYear(),
        nextMonthDate.getMonth(),
        1
      ),
    [nextMonthDate]
  );
  const nextTo = useMemo(
    () =>
      new Date(
        nextMonthDate.getFullYear(),
        nextMonthDate.getMonth() + 1,
        0,
        23,
        59,
        59
      ),
    [nextMonthDate]
  );

  const { data: meData } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.me.get(),
  });

  const { data: billingData } = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => api.billing.status(),
  });

  const { data: membersData } = useQuery({
    queryKey: ['calendars', calendarId, 'members'],
    queryFn: () => api.calendars.members(calendarId),
    enabled: Boolean(calendarId && !joinToken),
  });

  const currentCalendar = getCalendar(calendarId);

  // Access state comes from CalendarRouter (pre-resolved)
  const accessVia = accessViaProp ?? null;
  const isMember = isMemberProp ?? true;
  const isTemporaryVisitor =
    accessVia === 'join_token' && !isMember;
  const calColor =
    getCalendarColor(calendarId) ||
    currentCalendar?.color ||
    '#4a5568';

  const isNonPersonalCalendar =
    currentCalendar && !currentCalendar.is_personal;
  const myUserId = meData?.user?.id;
  const myRole = membersData?.members.find(
    (m: { user_id: string; role: string }) =>
      m.user_id === myUserId
  )?.role;

  const showTrialBanner =
    !trialBannerDismissed &&
    billingData?.plan.status === 'trial' &&
    Boolean(isNonPersonalCalendar) &&
    myRole === 'owner';

  const personalCalendarId =
    meData?.user?.personal_calendar_id;

  const createSubscription = useCreateSubscription();
  const { data: personalSubscriptions } = useSubscriptions(
    personalCalendarId ?? undefined
  );
  const existingSubscription = personalSubscriptions?.find(
    (sub: { source_calendar_id: string }) =>
      sub.source_calendar_id === calendarId
  );
  const isSubscribed = Boolean(existingSubscription);

  const showSubscribeButton =
    personalCalendarId &&
    calendarId !== personalCalendarId &&
    (isTemporaryVisitor
      ? Boolean(joinToken)
      : Boolean(currentCalendar?.join_token));

  const {
    data: events = [],
    isLoading: eventsIsLoading,
    isFetching: eventsIsFetching,
    isError: eventsIsError,
    error: eventsError,
    refetch: refetchEvents,
  } = useQuery({
    ...eventRangeQueryOptions(
      calendarId,
      from,
      to,
      joinToken
    ),
    select: (data: { events: unknown[] }) => data.events,
    enabled: Boolean(calendarId),
  });

  const expandedEvents = useMemo(
    () =>
      expandEvents(
        events as Parameters<typeof expandEvents>[0],
        from,
        to
      ),
    [events, from, to]
  );

  const { data: prevMonthEvents = [] } = useQuery({
    ...eventRangeQueryOptions(
      calendarId,
      prevFrom,
      prevTo,
      joinToken
    ),
    select: (data: { events: unknown[] }) => data.events,
    enabled: Boolean(calendarId),
  });

  const { data: nextMonthEvents = [] } = useQuery({
    ...eventRangeQueryOptions(
      calendarId,
      nextFrom,
      nextTo,
      joinToken
    ),
    select: (data: { events: unknown[] }) => data.events,
    enabled: Boolean(calendarId),
  });

  const expandedPrevMonthEvents = useMemo(
    () =>
      expandEvents(
        prevMonthEvents as Parameters<
          typeof expandEvents
        >[0],
        prevFrom,
        prevTo
      ),
    [prevMonthEvents, prevFrom, prevTo]
  );

  const expandedNextMonthEvents = useMemo(
    () =>
      expandEvents(
        nextMonthEvents as Parameters<
          typeof expandEvents
        >[0],
        nextFrom,
        nextTo
      ),
    [nextMonthEvents, nextFrom, nextTo]
  );

  const calendarColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const calId of visibleCalendarIds) {
      map[calId] = getCalendarColor(calId);
    }
    return map;
  }, [visibleCalendarIds, getCalendarColor]);

  const multiDaySegments =
    useMemo((): MultiDaySegment[] => {
      const MS_PER_DAY = 86400000;

      const multiDay = expandedEvents.filter(e => {
        if (e.all_day && e.start_date && e.end_date) {
          return e.start_date !== e.end_date;
        }
        const dur =
          new Date(e.end_at).getTime() -
          new Date(e.start_at).getTime();
        return dur > MS_PER_DAY;
      });

      if (multiDay.length === 0) return [];

      const monthStartDay = startOfDay(from);
      const monthEndDay = startOfDay(to);

      type RowStackMap = Map<number, number>;
      const rowStack: RowStackMap = new Map();

      const segments: MultiDaySegment[] = [];

      for (const event of multiDay) {
        let eventStart: Date;
        let eventEnd: Date;
        if (
          event.all_day &&
          event.start_date &&
          event.end_date
        ) {
          // Extract date part (API may return full timestamps)
          const sDate = event.start_date.slice(0, 10);
          const eDate = event.end_date.slice(0, 10);
          eventStart = startOfDay(
            new Date(sDate + 'T12:00')
          );
          eventEnd = startOfDay(new Date(eDate + 'T12:00'));
        } else {
          eventStart = startOfDay(new Date(event.start_at));
          eventEnd = startOfDay(new Date(event.end_at));
        }

        const clampedStart =
          eventStart < monthStartDay
            ? monthStartDay
            : eventStart;
        const clampedEnd =
          eventEnd > monthEndDay ? monthEndDay : eventEnd;

        const startDayOfMonth = Math.floor(
          (clampedStart.getTime() -
            monthStartDay.getTime()) /
            MS_PER_DAY
        );
        const endDayOfMonth = Math.floor(
          (clampedEnd.getTime() - monthStartDay.getTime()) /
            MS_PER_DAY
        );

        const firstGridPos =
          leadingBlanks + startDayOfMonth;
        const lastGridPos = leadingBlanks + endDayOfMonth;

        const firstRow = Math.floor(firstGridPos / 7);
        const lastRow = Math.floor(lastGridPos / 7);

        for (let row = firstRow; row <= lastRow; row++) {
          const rowStartGridPos = row * 7;
          const rowEndGridPos = rowStartGridPos + 6;

          const segStartGridPos = Math.max(
            firstGridPos,
            rowStartGridPos
          );
          const segEndGridPos = Math.min(
            lastGridPos,
            rowEndGridPos
          );

          const colStart =
            segStartGridPos - rowStartGridPos;
          const colSpan =
            segEndGridPos - segStartGridPos + 1;

          const stackKey = row;
          const stackIndex = rowStack.get(stackKey) ?? 0;
          rowStack.set(stackKey, stackIndex + 1);

          const color =
            (event as { subscription_color?: string })
              .subscription_color ??
            calendarColorMap[event.calendar_id] ??
            calColor;

          segments.push({
            event,
            color,
            rowIndex: row,
            colStart,
            colSpan,
            isStart: row === firstRow,
            isEnd: row === lastRow,
            stackIndex,
          });
        }
      }

      return segments;
    }, [
      expandedEvents,
      from,
      to,
      leadingBlanks,
      calendarColorMap,
      calColor,
    ]);

  function multiDayOnDay(day: number): ExpandedEvent[] {
    const target = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    return multiDayEventsMatchingDate(
      expandedEvents,
      target
    );
  }

  function eventsOnDay(day: number): ExpandedEvent[] {
    const target = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    return eventsMatchingDate(expandedEvents, target);
  }

  // Used for the faded leading/trailing overflow cells in the month grid,
  // which fall in the previous or next calendar month.
  function eventsOnOverflowDate(
    date: Date
  ): ExpandedEvent[] {
    const list =
      date < monthStart
        ? expandedPrevMonthEvents
        : expandedNextMonthEvents;
    return eventsMatchingDate(list, date);
  }

  function navigateToOverflowDate(date: Date) {
    setCurrentMonth(startOfMonth(date));
  }

  // Sync month to URL when it changes
  useEffect(() => {
    const year = currentMonth.getFullYear();
    const month = String(
      currentMonth.getMonth() + 1
    ).padStart(2, '0');
    const monthParam = `${year}-${month}`;

    const newParams = new URLSearchParams(searchParams);
    newParams.set('month', monthParam);
    setSearchParams(newParams, { replace: true });
  }, [currentMonth]);

  function prevMonth() {
    setCurrentMonth(
      m => new Date(m.getFullYear(), m.getMonth() - 1, 1)
    );
  }

  function nextMonth() {
    setCurrentMonth(
      m => new Date(m.getFullYear(), m.getMonth() + 1, 1)
    );
  }

  function goToToday() {
    setCurrentMonth(startOfMonth(new Date()));
  }

  function handleDayClick(day: number) {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    setEditEventId(null);
    setCreateForDate(null);
    setExpandedDay(date);
  }

  function closePanel() {
    setCreateForDate(null);
    setEditEventId(null);
    setEditOccurrenceDate(null);
  }

  function closeDayModal() {
    setExpandedDay(null);
  }

  // Shared controller for selecting an event to edit. Must be used by both
  // desktop (DayEventsModal) and mobile (MobileDayView) so the event id and
  // occurrence date are resolved the same way (e.g. for recurring instances).
  function selectEventForEdit(ev: ExpandedEvent) {
    const { eventId, occurrenceDate } =
      resolveEventForEdit(ev);
    setEditEventId(eventId);
    setEditOccurrenceDate(occurrenceDate ?? null);
  }

  function handleModalEventClick(ev: ExpandedEvent) {
    selectEventForEdit(ev);
    setExpandedDay(null);
  }

  function handleModalCreateEvent() {
    if (expandedDay) {
      setCreateForDate(expandedDay);
      setExpandedDay(null);
    }
  }

  const panelOpen =
    Boolean(createForDate) || Boolean(editEventId);

  const holidayRegion =
    meData?.user?.holiday_region ?? null;
  const holidaysByDay = useHolidaysByDay(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    holidayRegion
  );

  const value: CalendarPageContextValue = {
    calendarId,
    currentMonth,
    prevMonth,
    nextMonth,
    goToToday,
    accessVia,
    isMember,
    isTemporaryVisitor,
    joinToken: joinToken || null,
    createForDate,
    setCreateForDate,
    editEventId,
    setEditEventId,
    editOccurrenceDate,
    setEditOccurrenceDate,
    expandedDay,
    setExpandedDay,
    panelOpen,
    closePanel,
    closeDayModal,
    handleDayClick,
    selectEventForEdit,
    handleModalEventClick,
    handleModalCreateEvent,
    expandedEvents,
    eventsOnDay,
    multiDaySegments,
    multiDayOnDay,
    eventsOnOverflowDate,
    navigateToOverflowDate,
    leadingBlanks,
    totalDays,
    from,
    to,
    calendarColorMap,
    calColor,
    currentCalendar,
    showTrialBanner,
    trialBannerDismissed,
    setTrialBannerDismissed,
    billingData,
    holidayRegion,
    holidaysByDay,
    showSubscribeButton,
    isSubscribed,
    existingSubscription,
    createSubscription,
    navigate,
    eventsIsLoading,
    eventsIsFetching,
    eventsIsError,
    eventsError,
    refetchEvents,
  };

  // Invalidate calendars cache on mount when no calendarId is selected
  // (handled in CalendarPage before rendering this provider)

  return (
    <CalendarPageContext.Provider value={value}>
      {children}
    </CalendarPageContext.Provider>
  );
}
